// Import audio files
import tapSound from "./audio/tap.mp3";
import connectionSound from "./audio/4hz.mp3";
import connectedSound from "./audio/connected3.mp3";
import completeSound from "./audio/complete3.mp3";
import backgroundSound from "./audio/background.mp3";

export class SoundManager {
  constructor() {
    // Create audio context
    this.audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Initialize sound effects
    this.sounds = {
      tap: new Audio(tapSound),
      connected: new Audio(connectedSound),
      complete: new Audio(completeSound),
      background: new Audio(backgroundSound),
    };

    // Set up background music
    this.sounds.background.loop = true;

    // Track current path length for pitch adjustment
    this.currentPathLength = 0;

    // Buffer for connection sound
    this.connectionBuffer = null;
    this.loadConnectionSound();

    // Keep track of active connection sounds
    this.activeConnections = new Set();

    // Load and apply audio settings from local storage
    this.loadAudioSettings();
  }

  async loadConnectionSound() {
    try {
      const response = await fetch(connectionSound);
      const arrayBuffer = await response.arrayBuffer();
      this.connectionBuffer = await this.audioContext.decodeAudioData(
        arrayBuffer
      );
    } catch (error) {
      console.error("Failed to load connection sound:", error);
    }
  }

  loadAudioSettings() {
    const defaultSettings = {
      musicEnabled: true,
      musicVolume: 0.5,
      sfxEnabled: true,
      sfxVolume: 0.5,
    };

    // Load settings from local storage
    const savedSettings = localStorage.getItem("audioSettings");
    const settings = savedSettings
      ? JSON.parse(savedSettings)
      : defaultSettings;

    // Apply music settings
    this.sounds.background.volume = settings.musicEnabled
      ? settings.musicVolume
      : 0;

    // Store sfx volume for Web Audio API sounds
    this.sfxVolume = settings.sfxEnabled ? settings.sfxVolume : 0;

    // Apply SFX settings only to sounds that exist in this.sounds
    ["tap", "connected", "complete"].forEach((sound) => {
      if (this.sounds[sound]) {
        this.sounds[sound].volume = settings.sfxEnabled
          ? settings.sfxVolume
          : 0;
      }
    });
  }

  // Reset path length counter
  resetPathLength() {
    this.currentPathLength = 0;
  }

  // Play background music
  playBackgroundMusic() {
    this.sounds.background.play().catch((error) => {
      console.log("Background music playback failed:", error);
    });
  }

  // Stop background music
  stopBackgroundMusic() {
    this.sounds.background.pause();
    this.sounds.background.currentTime = 0;
  }

  // Play tap sound
  playTap() {
    this.sounds.tap.currentTime = 0;
    this.sounds.tap.play().catch((error) => {
      console.log("Tap sound playback failed:", error);
    });
  }

  // Play connection sound with smooth pitch increase
  async playConnection() {
    if (!this.connectionBuffer) {
      console.warn("Connection sound not loaded yet");
      return;
    }

    // Calculate pitch increase (more subtle than before)
    this.currentPathLength++;
    const pitchIncrease = Math.min(this.currentPathLength * 0.05, 0.3); // More subtle increase, capped at 0.3
    const playbackRate = 1 + pitchIncrease;

    // Create audio source and gain node
    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();

    source.buffer = this.connectionBuffer;
    source.playbackRate.value = playbackRate;

    // Apply smooth fade in/out to prevent clicks
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(
      this.sfxVolume * 0.5, // Use stored sfx volume
      this.audioContext.currentTime + 0.01
    );
    gainNode.gain.linearRampToValueAtTime(
      0,
      this.audioContext.currentTime + this.connectionBuffer.duration
    );

    // Connect nodes
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Start playback
    source.start();

    // Clean up when done
    source.onended = () => {
      source.disconnect();
      gainNode.disconnect();
      this.activeConnections.delete(source);
    };

    // Keep track of active connection
    this.activeConnections.add(source);
  }

  // Play connected sound
  playConnected() {
    this.sounds.connected.currentTime = 0;
    this.sounds.connected.play().catch((error) => {
      console.log("Connected sound playback failed:", error);
    });
    // Reset path length after completing a connection
    this.resetPathLength();
  }

  // Play complete sound
  playComplete() {
    this.sounds.complete.currentTime = 0;
    this.sounds.complete.play().catch((error) => {
      console.log("Complete sound playback failed:", error);
    });
  }
}
